// Magic Rent Price Component - One button, perfect price!
import React, { useState } from "react";
import PropTypes from "prop-types";
import { Card, Button, Typography, Box, CircularProgress } from "@mui/material";
import { AttachMoney, AutoAwesome } from "@mui/icons-material";
import { propertyService } from "services/firestoreService";
import { useAuth } from "contexts/AuthContext";

function MagicRentPrice({ propertyId, currentRent }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [magicPrice, setMagicPrice] = useState(null);
  const [showResult, setShowResult] = useState(false);

  const getMagicPrice = async () => {
    setLoading(true);
    try {
      // Fetch all user properties and compute average rent from similar ones
      const property = await propertyService.getById(propertyId);
      const allProperties = await propertyService.getAll(user.uid);

      // Filter comparable properties (same type, exclude current)
      const comparables = allProperties.filter(
        (p) =>
          p.id !== propertyId &&
          p.monthly_rent > 0 &&
          (!property.property_type || p.property_type === property.property_type)
      );

      let magicPriceValue;
      let confidence;
      let basedOn = comparables.length;

      if (comparables.length >= 5) {
        const total = comparables.reduce((sum, p) => sum + p.monthly_rent, 0);
        magicPriceValue = Math.round(total / comparables.length);
        confidence = "High";
      } else if (comparables.length >= 2) {
        const total = comparables.reduce((sum, p) => sum + p.monthly_rent, 0);
        magicPriceValue = Math.round(total / comparables.length);
        confidence = "Medium";
      } else if (comparables.length === 1) {
        magicPriceValue = comparables[0].monthly_rent;
        confidence = "Low";
      } else {
        // No comparables, use current rent or property rent as baseline
        magicPriceValue = currentRent || property.monthly_rent || 0;
        confidence = "Low";
        basedOn = 0;
      }

      setMagicPrice({
        magic_price: magicPriceValue,
        confidence,
        based_on: basedOn,
      });
      setShowResult(true);
    } catch (error) {
      console.error("Magic failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const getConfidenceColor = (confidence) => {
    switch (confidence) {
      case "High":
        return "success.main";
      case "Medium":
        return "warning.main";
      case "Low":
        return "error.main";
      default:
        return "text.secondary";
    }
  };

  return (
    <Card sx={{ p: 3, textAlign: "center", position: "relative", overflow: "visible" }}>
      {!showResult ? (
        <>
          <Typography variant="h5" gutterBottom>
            Magic Rent Price
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            One click to find the perfect rent price
          </Typography>

          <Button
            variant="contained"
            size="large"
            onClick={getMagicPrice}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <AutoAwesome />}
            sx={{
              py: 2,
              px: 4,
              fontSize: "1.2rem",
              background: "linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)",
              boxShadow: "0 3px 5px 2px rgba(255, 105, 135, .3)",
              "&:hover": {
                background: "linear-gradient(45deg, #FE6B8B 60%, #FF8E53 90%)",
                transform: "scale(1.05)",
              },
              transition: "all 0.3s ease",
            }}
          >
            {loading ? "Working Magic..." : "Get Magic Price"}
          </Button>
        </>
      ) : (
        <Box>
          <AutoAwesome sx={{ fontSize: 40, color: "warning.main", mb: 2 }} />

          <Typography variant="h3" sx={{ fontWeight: "bold", mb: 1 }}>
            ${magicPrice.magic_price}
            <Typography component="span" variant="h5" color="text.secondary">
              /month
            </Typography>
          </Typography>

          {currentRent && (
            <Typography
              variant="body1"
              sx={{
                mb: 2,
                color: magicPrice.magic_price > currentRent ? "success.main" : "error.main"
              }}
            >
              {magicPrice.magic_price > currentRent
                ? `↑ $${magicPrice.magic_price - currentRent} more than current`
                : `↓ $${currentRent - magicPrice.magic_price} less than current`
              }
            </Typography>
          )}

          <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 2 }}>
            <Typography variant="body2">
              Confidence:
              <Typography
                component="span"
                sx={{
                  ml: 1,
                  fontWeight: "bold",
                  color: getConfidenceColor(magicPrice.confidence)
                }}
              >
                {magicPrice.confidence}
              </Typography>
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Based on {magicPrice.based_on} properties
            </Typography>
          </Box>

          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setShowResult(false);
              setMagicPrice(null);
            }}
            sx={{ mt: 2 }}
          >
            Try Again
          </Button>
        </Box>
      )}
    </Card>
  );
}

MagicRentPrice.propTypes = {
  propertyId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]).isRequired,
  currentRent: PropTypes.number
};

MagicRentPrice.defaultProps = {
  currentRent: null
};

export default MagicRentPrice;
